/**
 * Configuración general de la aplicación
 */

/// <reference types="vite/client" />

interface AlchemyKeys {
  ethereum: string;
  polygon: string;
  arbitrum: string;
  optimism: string;
  goerli: string;
}

interface StorageKeys {
  wallet: string;
  theme: string;
  language: string;
  hasWallet: string;
  encryptedWallet: string;
  session: string;
  selectedNetwork: string;
  deviceId: string;
  lastSync: string;
}

interface SecurityConfig {
  sessionDuration: number;
  minPasswordLength: number;
  inactivityTimeout: number;
  alwaysRequirePasswordForTx: boolean;
  devPrivateKey?: string;
}

interface AppConfig {
  appVersion: string;
  appName: string;
  apiBaseUrl: string;
  swapEnabled: boolean;
  isDevelopment: boolean;
  alchemyKeys: AlchemyKeys;
  monitoringInterval: number;
  storageKeys: StorageKeys;
  security: SecurityConfig;
}

// Configuración básica de la aplicación
export const appConfig: AppConfig = {
  appVersion: import.meta.env.VITE_APP_VERSION || '1.3.0',
  appName: 'Multi-Chain Wallet',

  // Vacío = rutas relativas (web: mismo origen que sirve el backend). Una app
  // empaquetada (Capacitor) no tiene servidor co-ubicado, así que ahí necesita
  // una URL absoluta puesta en build-time vía VITE_API_BASE_URL.
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL || '',

  // Vista Swap: oculta (sin entrada en el sidebar y /swap redirige al dashboard)
  // salvo que VITE_ENABLE_SWAP sea exactamente 'true'.
  swapEnabled: import.meta.env.VITE_ENABLE_SWAP === 'true',

  // Modo de desarrollo (true para desarrollo, false para producción)
  // IMPORTANTE: Debe establecerse en false para producción
  isDevelopment: import.meta.env.DEV || false,

  // Configuración de Alchemy API para transacciones
  alchemyKeys: {
    ethereum: import.meta.env.VITE_ALCHEMY_KEY_ETHEREUM || 'tKltxuh3ZtxQFQHjOfiOPs_kUi5fqVgZ',
    polygon: import.meta.env.VITE_ALCHEMY_KEY_POLYGON || 'tKltxuh3ZtxQFQHjOfiOPs_kUi5fqVgZ',
    arbitrum: import.meta.env.VITE_ALCHEMY_KEY_ARBITRUM || 'tKltxuh3ZtxQFQHjOfiOPs_kUi5fqVgZ',
    optimism: import.meta.env.VITE_ALCHEMY_KEY_OPTIMISM || 'tKltxuh3ZtxQFQHjOfiOPs_kUi5fqVgZ',
    goerli: import.meta.env.VITE_ALCHEMY_KEY_GOERLI || 'tKltxuh3ZtxQFQHjOfiOPs_kUi5fqVgZ',
  },

  // Intervalo para comprobación de transacciones (15 segundos)
  monitoringInterval: 15 * 1000,

  // Configuración del almacenamiento
  storageKeys: {
    wallet: 'multichain_wallet',
    theme: 'multichain_theme',
    language: 'multichain_language',
    hasWallet: 'multichain_has_wallet',
    encryptedWallet: 'multichain_encrypted_wallet',
    session: 'multichain_wallet_session',
    selectedNetwork: 'multichain_selected_network',
    deviceId: 'wallet_device_id',
    lastSync: 'wallet_last_sync',
  },

  // Configuración de seguridad
  security: {
    sessionDuration: 24 * 60 * 60 * 1000, // 24 horas en milisegundos
    minPasswordLength: 8, // Longitud mínima de contraseña
    inactivityTimeout: parseInt(import.meta.env.VITE_INACTIVITY_TIMEOUT) || 15 * 60 * 1000, // 15 minutos
    alwaysRequirePasswordForTx: false, // No requerir contraseña para transacciones

    // Clave privada para desarrollo (SOLO USAR EN DESARROLLO)
    devPrivateKey: import.meta.env.DEV
      ? '0xac0974bec39a17e36ba4a6b4d238ff944bacb478cbed5efcae784d7bf4f2ff80'
      : undefined,
  },
};

/**
 * Obtiene la versión de la aplicación
 */
export const getAppVersion = (): string => {
  return appConfig.appVersion;
};

/**
 * Obtiene el nombre de la aplicación
 */
export const getAppName = (): string => {
  return appConfig.appName;
};

/**
 * Prefijo para las llamadas al backend propio (vacío en web, URL absoluta en apps empaquetadas).
 */
export const getApiBaseUrl = (): string => {
  return appConfig.apiBaseUrl;
};

/**
 * Indica si la vista Swap está habilitada (VITE_ENABLE_SWAP)
 */
export const isSwapEnabled = (): boolean => {
  return appConfig.swapEnabled;
};

/**
 * Obtiene la clave de API de Alchemy para una red específica
 */
export const getAlchemyKey = (networkId: string): string | null => {
  return appConfig.alchemyKeys[networkId as keyof AlchemyKeys] || null;
};

/**
 * Obtiene el intervalo de monitoreo de transacciones
 */
export const getMonitoringInterval = (): number => {
  return appConfig.monitoringInterval;
};

/**
 * Obtiene la clave de almacenamiento para un tipo específico
 */
export const getStorageKey = (key: keyof StorageKeys): string | null => {
  return appConfig.storageKeys[key] || null;
};

/**
 * Obtiene la duración de la sesión
 */
export const getSessionDuration = (): number => {
  return appConfig.security.sessionDuration;
};

/**
 * Obtiene la longitud mínima de contraseña
 */
export const getMinPasswordLength = (): number => {
  return appConfig.security.minPasswordLength;
};

/**
 * Obtiene el tiempo de inactividad antes de bloqueo
 */
export const getInactivityTimeout = (): number => {
  return appConfig.security.inactivityTimeout;
};

/**
 * Verifica si siempre se debe requerir contraseña para transacciones
 */
export const getAlwaysRequirePasswordForTx = (): boolean => {
  return appConfig.security.alwaysRequirePasswordForTx;
};

/**
 * Verifica si la aplicación está en modo desarrollo
 */
export const isDevelopmentMode = (): boolean => {
  return appConfig.isDevelopment === true;
};

/**
 * Obtiene la clave privada de desarrollo (solo en modo desarrollo)
 */
export const getDevPrivateKey = (): string | null => {
  // Solo devolver la clave si estamos en modo desarrollo
  if (isDevelopmentMode() && appConfig.security.devPrivateKey) {
    return appConfig.security.devPrivateKey;
  }
  return null;
};
